import { useState } from 'react';
import {
  getFormProps,
  getTextareaProps,
  useForm,
  type SubmissionResult,
} from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import { useHistory, useLocation } from 'react-router-dom';
import { z } from 'zod';
import { Toast, ToastVariant, useToast } from '@hungryroot/components-toast';
import { RequireCheckedOut } from '@hungryroot/customer';
import { fullPageRedirect, windowVars } from '@hungryroot/environment';
import { Loading } from '@hungryroot/loading-component-primitives';
import { logExceptionOnce, logUnknownErrorResponse } from '@hungryroot/o11y';
import { FINANCIAL_INCENTIVE_SECTION_ID } from '@hungryroot/page-privacy';
import {
  AuthState,
  useCustomerSelfAddress,
  useIdentify,
  usePostCustomerAccountReferrals,
  validateErrorResponse,
} from '@hungryroot/rest-hooks';
import {
  ACCOUNT_REFERRAL_ROUTE,
  LOGIN_ROUTE,
  ROUTE_QUIZ_HELLO,
} from '@hungryroot/routes';
import {
  Button,
  Heading,
  Paragraph,
  TextArea,
  TextInput,
} from '@hungryroot/ui-kit';
import {
  MAX_GIFTS_PER_SEND,
  REFERRAL_BONUS,
  REFERRAL_DISCOUNT,
} from '../../components/ReferralBanner/referral';
import styles from './ReferralPage.module.css';

const FINANCIAL_INCENTIVE_URL = `/privacy/#${FINANCIAL_INCENTIVE_SECTION_ID}`;
const SPLIT_EMAILS_REG_EXP = /[,;\s]+/;
const EMAIL_PLACEHOLDER =
  'adam@example.com,betty@example.com,charlie@example.com';
const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again later.';
const SUCCESS_MESSAGE = `Success! You'll receive $${REFERRAL_BONUS} in your account when they place an order.`;
const EmailAddressSchema = z.string().email();
const FACEBOOK_SDK_SCRIPT_ID = 'facebook-jssdk';
const FACEBOOK_SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';
const TWITTER_DEFAULT_MESSAGE = `Hungryroot helps me shop for, cook, and love healthy food. Use this link for $${REFERRAL_DISCOUNT} off your first grocery delivery.`;

let facebookSdkPromise: Promise<fb.FacebookStatic> | undefined;

type LocationHash = '#email' | '#facebook' | '#twitter';

const ReferralFormSchema = z.object({
  emails: z
    .string({
      required_error: 'Enter at least one email address.',
    })
    .trim()
    .min(1, 'Enter at least one email address.')
    .superRefine((value, ctx) => {
      const emails = splitEmails(value);

      if (emails.length > MAX_GIFTS_PER_SEND) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Enter ${MAX_GIFTS_PER_SEND} or fewer email addresses.`,
        });
      }

      if (
        emails.some((email) => !EmailAddressSchema.safeParse(email).success)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Enter valid email addresses.',
        });
      }
    })
    .transform(splitEmails),
  message: z.string().trim().optional().default(''),
});

export function ReferralPage() {
  return (
    <RequireCheckedOut
      onNotCheckedOut={() => fullPageRedirect(ROUTE_QUIZ_HELLO)}
      onNotLoggedIn={() => {
        fullPageRedirect(`${LOGIN_ROUTE}?next=${ACCOUNT_REFERRAL_ROUTE}`);
      }}
    >
      <ReferralPageContent />
    </RequireCheckedOut>
  );
}

export function ReferralPageContent() {
  const identify = useIdentify();
  const customerAddress = useCustomerSelfAddress();

  if (
    identify.authState !== AuthState.isAuthenticated ||
    customerAddress.isLoading ||
    customerAddress.data === undefined
  ) {
    return (
      <div className={styles['page-shell']}>
        <Loading />
      </div>
    );
  }

  return (
    <ReferralForm
      customer={identify.customerData}
      customerState={customerAddress.data.state}
    />
  );
}

function ReferralForm({ customer, customerState }: ReferralFormProps) {
  const history = useHistory();
  const location = useLocation();
  const postReferral = usePostCustomerAccountReferrals();
  const { toast } = useToast();
  const [lastResult, setLastResult] = useState<
    SubmissionResult<string[]> | undefined
  >();
  const [copyStatus, setCopyStatus] = useState<string | undefined>();
  const [twitterText, setTwitterText] = useState(TWITTER_DEFAULT_MESSAGE);

  const referralUrl = `${windowVars.getOrigin()}/r/${customer.referral_code}`;
  const defaultMessage = getDefaultMessage(customer.referral_code);
  const activeHash = getActiveLocationHash(location.hash);

  const [form, fields] = useForm({
    lastResult,
    id: 'referral-form',
    constraint: getZodConstraint(ReferralFormSchema),
    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput',
    defaultValue: {
      emails: '',
      message: defaultMessage,
    },
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: ReferralFormSchema });
    },
    onSubmit: async (event, { formData }) => {
      event.preventDefault();

      const submission = parseWithZod(formData, {
        schema: ReferralFormSchema,
      });

      if (submission.status !== 'success') {
        setLastResult(submission.reply());
        return;
      }

      trackReferralEvent('email');

      try {
        await postReferral.mutateAsync({
          customerId: customer.id,
          referrals: {
            message: submission.value.message,
            recipients: submission.value.emails.map((email) => {
              return { email };
            }),
          },
        });
        setLastResult(undefined);
        toast(
          <Toast testid="toast-referral-success" variant={ToastVariant.Success}>
            {SUCCESS_MESSAGE}
          </Toast>
        );
      } catch (error) {
        const knownError = validateErrorResponse(error);
        const errorMessage =
          knownError?.data.msg_id === 'noemails'
            ? 'Enter at least one email address.'
            : knownError?.data.msg_id === 'overlimit'
              ? `Enter ${MAX_GIFTS_PER_SEND} or fewer email addresses.`
              : GENERIC_ERROR_MESSAGE;

        if (knownError === null) {
          logUnknownErrorResponse('Failed to send referral email', {
            key: 'ReferralPage.submitReferral',
            error: JSON.stringify(error),
          });
        }

        toast(
          <Toast testid="toast-referral-error" variant={ToastVariant.Critical}>
            {errorMessage}
          </Toast>
        );
      }
    },
  });
  const emailTextareaProps = getTextareaProps(fields.emails);

  return (
    <main className={styles['page-shell']}>
      <section className={styles.card}>
        <div className={styles.header}>
          <Heading variant="H2">
            Give friends ${REFERRAL_DISCOUNT} off Hungryroot
          </Heading>
          <Paragraph>
            When they place their first grocery delivery of $100 or more, we'll
            say thanks by adding ${REFERRAL_BONUS} to your account.
          </Paragraph>
        </div>

        <div className={styles['tabs-column']}>
          <nav className={styles.tabs} aria-label="Referral sharing options">
            <ReferralTab
              activeHash={activeHash}
              ariaLabel="Email Icon To Show Referral Sharing via Email"
              hash="#email"
              icon={String.fromCharCode(0xf0e0)}
              onClick={() => history.push('#email')}
            />
            <ReferralTab
              activeHash={activeHash}
              ariaLabel="Facebook Icon To Show Referral Sharing via Facebook"
              hash="#facebook"
              icon={String.fromCharCode(0xf09a)}
              onClick={() => history.push('#facebook')}
            />
            <ReferralTab
              activeHash={activeHash}
              ariaLabel="Twitter Icon To Show Referral Sharing via Twitter"
              hash="#twitter"
              icon={String.fromCharCode(0xf099)}
              onClick={() => history.push('#twitter')}
            />
          </nav>

          <div className={styles['tab-panel']}>
            <div className={styles['form-column']}>
              {activeHash === '#email' ? (
                <form className={styles.form} {...getFormProps(form)}>
                  <TextArea
                    label="Enter your friends' email addresses:"
                    textAreaProps={{
                      ...emailTextareaProps,
                      required:
                        fields.emails.errors !== undefined
                          ? emailTextareaProps.required
                          : undefined,
                    }}
                    placeholder={EMAIL_PLACEHOLDER}
                    rows={3}
                    errorMessage={fields.emails.errors}
                    helpText={`Separate multiple emails with commas. (max ${MAX_GIFTS_PER_SEND} emails per share)`}
                  />

                  <TextArea
                    label="Enter a personalized message:"
                    textAreaProps={getTextareaProps(fields.message)}
                    rows={5}
                    defaultValue={defaultMessage}
                    errorMessage={fields.message.errors}
                    helpText="Optional"
                  />

                  <Button
                    style="default"
                    prominence="primary"
                    size="large"
                    width="full-width-mobile"
                    loading={postReferral.isPending}
                    disabled={postReferral.isPending}
                  >
                    Share via email
                  </Button>
                </form>
              ) : null}

              {activeHash === '#facebook' ? (
                <form
                  className={styles.form}
                  onSubmit={async (event) => {
                    event.preventDefault();

                    try {
                      await shareOnFacebook(referralUrl);
                    } catch (error) {
                      logExceptionOnce(error, {
                        key: 'ReferralPage.shareOnFacebook',
                      });
                    }
                  }}
                >
                  <div className={styles.sample}>
                    <img
                      alt=""
                      className={styles['sample-image']}
                      src="/public/img/referral/referral_img.jpg"
                    />
                    <Paragraph className={styles['facebook-description']}>
                      Hungryroot helps me shop for, cook, and love healthy food.
                      Use this link for ${REFERRAL_DISCOUNT} off your first
                      grocery delivery.
                    </Paragraph>
                  </div>
                  <Button
                    style="default"
                    prominence="primary"
                    size="large"
                    width="full-width-mobile"
                  >
                    Share via Facebook
                  </Button>
                </form>
              ) : null}

              {activeHash === '#twitter' ? (
                <form
                  className={styles.form}
                  onSubmit={(event) => {
                    event.preventDefault();
                    shareOnTwitter(twitterText);
                  }}
                >
                  <TextArea
                    label="Enter a personalized message:"
                    rows={5}
                    defaultValue={twitterText}
                    onChange={setTwitterText}
                  />
                  <Button
                    style="default"
                    prominence="primary"
                    size="large"
                    width="full-width-mobile"
                  >
                    Share via Twitter
                  </Button>
                </form>
              ) : null}
            </div>
            <div className={styles['referral-link']}>
              <TextInput
                label="Or manually copy & paste the URL below"
                value={referralUrl}
                onChange={() => undefined}
                isReadOnly
                inputProps={{
                  onClick: async () => {
                    await copyReferralUrl(referralUrl);
                    setCopyStatus('Copied to clipboard');
                  },
                }}
              />
              {copyStatus ? (
                <Paragraph className={styles['copy-status']} role="status">
                  {copyStatus}
                </Paragraph>
              ) : null}
            </div>
          </div>
        </div>

        {customerState === 'CA' ? (
          <Paragraph className={styles.disclosure} variant="caption">
            Under California law, referral programs may be considered a
            financial incentive. By clicking "{getDisclosureService(activeHash)}
            ," you acknowledge that you have read, understand, and agree to the
            terms set forth in our{' '}
            <a href="https://www.hungryroot.com/privacy/">
              Privacy Policy
            </a> and{' '}
            <a href={FINANCIAL_INCENTIVE_URL}>Notice of Financial Incentive</a>.
          </Paragraph>
        ) : null}
      </section>
    </main>
  );
}

function ReferralTab({
  activeHash,
  ariaLabel,
  hash,
  icon,
  onClick,
}: ReferralTabProps) {
  return (
    <a
      aria-label={ariaLabel}
      aria-current={activeHash === hash ? 'page' : undefined}
      className={activeHash === hash ? styles['tab-active'] : styles.tab}
      href={hash}
      onClick={(event) => {
        event.preventDefault();
        onClick();
      }}
      title={ariaLabel}
    >
      {icon}
    </a>
  );
}

interface ReferralFormProps {
  customer: {
    id: number;
    referral_code: string;
  };
  customerState: string | null | undefined;
}

interface ReferralTabProps {
  activeHash: LocationHash;
  ariaLabel: string;
  hash: LocationHash;
  icon: string;
  onClick: () => void;
}

function splitEmails(value: string) {
  return value.split(SPLIT_EMAILS_REG_EXP).filter(Boolean);
}

function getDefaultMessage(referralCode: string) {
  return `Use special code ${referralCode} to get $${REFERRAL_BONUS} off a delivery of $100+ from Hungryroot, a personalized grocery service that makes it easy to shop for, cook, and love healthy food.`;
}

function getActiveLocationHash(locationHash: string): LocationHash {
  if (
    locationHash === '#email' ||
    locationHash === '#facebook' ||
    locationHash === '#twitter'
  ) {
    return locationHash;
  }

  return '#email';
}

function getDisclosureService(activeHash: LocationHash) {
  if (activeHash === '#facebook') return 'Share Via Facebook';
  if (activeHash === '#twitter') return 'Share Via Twitter';
  return 'Share Via Email';
}

async function shareOnFacebook(referralUrl: string) {
  const facebookSdk = await loadFacebookSdk();

  facebookSdk.ui(
    {
      method: 'share',
      href: referralUrl,
    },
    (response) => {
      if (response?.error_message !== undefined) {
        trackReferralEvent('facebook-error');
        return;
      }

      trackReferralEvent('facebook');
    }
  );
}

function loadFacebookSdk() {
  if (window.FB !== undefined) return Promise.resolve(window.FB);
  if (facebookSdkPromise !== undefined) return facebookSdkPromise;

  facebookSdkPromise = new Promise<fb.FacebookStatic>((resolve, reject) => {
    window.fbAsyncInit = function () {
      FB.init({
        appId: process.env.PUBLIC_FACEBOOK_APP_ID ?? '',
        cookie: true,
        status: true,
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v6.0',
      });

      resolve(FB);
    };

    const existingScript = document.getElementById(FACEBOOK_SDK_SCRIPT_ID);
    if (existingScript !== null) return;

    const script = document.createElement('script');
    script.id = FACEBOOK_SDK_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = FACEBOOK_SDK_SRC;
    script.dataset.testid = 'facebook-sdk-script';
    script.addEventListener(
      'error',
      () => {
        facebookSdkPromise = undefined;
        reject(new Error('Failed to load Facebook SDK'));
      },
      { once: true }
    );

    document.body.appendChild(script);
  });

  return facebookSdkPromise;
}

function shareOnTwitter(message: string) {
  window.open(
    `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`,
    '_blank'
  );
  trackReferralEvent('twitter');
}

function trackReferralEvent(
  service: 'email' | 'facebook' | 'facebook-error' | 'twitter'
) {
  if (typeof ga !== 'undefined') {
    ga('send', 'event', 'referral', service);
  }

  if (service !== 'facebook-error') {
    window.dataLayer?.push({
      event: `hr.referral.${service}`,
    });
  }
}

async function copyReferralUrl(referralUrl: string) {
  try {
    await navigator.clipboard.writeText(referralUrl);
  } catch {
    const input = document.createElement('input');
    input.value = referralUrl;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
  }
}
